import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined'
import { Box, Container, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AlertComponent } from 'src/components/Alert/AlertComponent'
import useProtectedPage from 'src/hooks/useProtectedPage'
import { FileRepositoryModel } from 'src/models/file-repository-model'
import { useFileRepository } from './hooks/useFileRepository'
import { useDirectoryChildCounts } from './hooks/useDirectoryChildCounts'
import { FileRepositoryToolbar } from './components/FileRepositoryToolbar'
import { FileRepositoryBreadcrumbs } from './components/FileRepositoryBreadcrumbs'
import { FileRepositoryGrid } from './components/FileRepositoryGrid'
import { CreateDirectoryModal } from './components/CreateDirectoryModal'
import { UploadFileModal } from './components/UploadFileModal'
import { RenameEntryModal } from './components/RenameEntryModal'
import { DeleteEntryModal } from './components/DeleteEntryModal'
import { MoveEntryModal } from './components/MoveEntryModal'
import { FilePreviewModal } from './components/FilePreviewModal'

export function FileRepositoryPage() {
  useProtectedPage()
  const { t } = useTranslation()
  const fileRepo = useFileRepository()
  const directoryChildCounts = useDirectoryChildCounts(
    fileRepo.entries,
    fileRepo.tecnoitCompanyId
  )

  // ── Modal state ────────────────────────────────────────────
  const [createDirOpen, setCreateDirOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [renameEntry, setRenameEntry] = useState<FileRepositoryModel | null>(null)
  const [deleteEntry, setDeleteEntry] = useState<FileRepositoryModel | null>(null)
  const [moveEntry, setMoveEntry] = useState<FileRepositoryModel | null>(null)
  const [previewEntry, setPreviewEntry] = useState<FileRepositoryModel | null>(null)

  // ── Handlers ───────────────────────────────────────────────
  const handleFileClick = (entry: FileRepositoryModel) => {
    if (entry.mimeType?.startsWith('image/') || entry.mimeType === 'video/mp4') {
      setPreviewEntry(entry)
    } else {
      // CSV or other: trigger download
      fileRepo.handleDownload(entry)
    }
  }

  return (
    <Container sx={{ mt: '96px' }}>
      <AlertComponent />

      {/* Page header */}
      <Box
        component="div"
        sx={{
          color: '#FF5E1E',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-start',
          mb: '24px',
        }}
      >
        <FolderCopyOutlinedIcon sx={{ position: 'relative', top: '3px' }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: '500',
            fontSize: '20px',
            lineHeight: '160%',
            letterSpacing: '0.15px',
            color: '#FF5E1E',
          }}
        >
          {t('fileRepository.title')}
        </Typography>
      </Box>

      {/* Toolbar: Upload, New Folder, Search, Sort */}
      <FileRepositoryToolbar
        canCreate={fileRepo.canCreate}
        search={fileRepo.search}
        onSearchChange={fileRepo.setSearch}
        sortBy={fileRepo.sortBy}
        sortOrder={fileRepo.sortOrder}
        onSortByChange={fileRepo.setSortBy}
        onSortOrderChange={fileRepo.setSortOrder}
        onUploadClick={() => setUploadOpen(true)}
        onNewFolderClick={() => setCreateDirOpen(true)}
      />

      {/* Breadcrumbs */}
      <FileRepositoryBreadcrumbs
        breadcrumbs={fileRepo.breadcrumbs}
        onNavigate={fileRepo.navigateToBreadcrumb}
      />

      {/* Main grid */}
      <FileRepositoryGrid
        entries={fileRepo.entries}
        loading={fileRepo.loading}
        total={fileRepo.total}
        page={fileRepo.page}
        limit={fileRepo.limit}
        directoryChildCounts={directoryChildCounts}
        onPageChange={fileRepo.setPage}
        onDirectoryClick={fileRepo.navigateToDirectory}
        onFileClick={handleFileClick}
        onRenameClick={(entry) => setRenameEntry(entry)}
        onMoveClick={(entry) => setMoveEntry(entry)}
        onDeleteClick={(entry) => setDeleteEntry(entry)}
        onDownload={fileRepo.handleDownload}
        canUpdate={fileRepo.canUpdate}
        canDelete={fileRepo.canDelete}
        canDownload={fileRepo.canDownload}
      />

      {/* ── Modals ──────────────────────────────────────────── */}
      <CreateDirectoryModal
        open={createDirOpen}
        onClose={() => setCreateDirOpen(false)}
        onCreate={(name, description, companyId) =>
          fileRepo.handleCreateDirectory(name, description, companyId)
        }
      />

      <UploadFileModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={(file, description, companyId) =>
          fileRepo.handleUpload(file, description, companyId)
        }
      />

      <RenameEntryModal
        open={Boolean(renameEntry)}
        entry={renameEntry}
        onClose={() => setRenameEntry(null)}
        onRename={fileRepo.handleRename}
      />

      <DeleteEntryModal
        open={Boolean(deleteEntry)}
        entry={deleteEntry}
        onClose={() => setDeleteEntry(null)}
        onDelete={fileRepo.handleDelete}
      />

      <MoveEntryModal
        open={Boolean(moveEntry)}
        entry={moveEntry}
        onClose={() => setMoveEntry(null)}
        onMove={fileRepo.handleMove}
      />

      <FilePreviewModal
        open={Boolean(previewEntry)}
        entry={previewEntry}
        onClose={() => setPreviewEntry(null)}
        onDownload={fileRepo.handleDownload}
        canDownload={fileRepo.canDownload}
      />
    </Container>
  )
}
